import { MouseEvent, useMemo, useState } from "react"
import { Box, Checkbox, IconButton, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock, faLockOpen, faTruck } from "@fortawesome/pro-light-svg-icons"
import type { QueryClient } from "@tanstack/react-query"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { changeLockStatusMerchant, MerchantMeetingServiceOrdersQuery } from "../../meetingServiceApi"
import {
    DeliveryType,
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrdersView,
} from "../../meetingServiceTypes"
import { getHiddenOrderStatuses } from "../../meetingServiceLib"
import { MinimizedStatusButton, StatusButton } from "../../components/StatusMenu"
import { StatusChangeModal } from "../../components/StatusChangeModal"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { ProductionClampedName } from "./ProductionClampedName"

const useStyles = makeStyles()((theme) => ({
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".625rem",
        boxSizing: "border-box",
        height: "4.5rem",
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: ".625rem",
        minWidth: 0,
    },
    selectCheckbox: {
        width: "2.375rem",
        height: "2.375rem",
    },
    titleBlock: {
        display: "flex",
        alignItems: "center",
        gap: "1.5rem",
        minWidth: 0,
    },
    title: {
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: ".009375rem",
        color: "#1A2027",
    },
    delivery: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
    },
    deliveryIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
    },
    deliveryText: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: ".009375rem",
        color: theme.palette.tailwind.slate[800],
        whiteSpace: "nowrap",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "1rem",
    },
    statusButtonWrap: {
        display: "inline-flex",
        "& .MuiButton-root": {
            height: "3rem",
            padding: ".875rem 1rem",
            borderRadius: ".75rem",
        },
    },
    lockButton: {
        width: "3rem",
        height: "3rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    lockedIcon: {
        color: theme.palette.tailwind.slate[700],
    },
    unlockedIcon: {
        color: theme.palette.tailwind.green[500],
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    isSelected: boolean
    onToggleSelect: () => void
    view: MeetingServiceOrdersView
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

export function ProductionOrderCardHeader({
    order,
    isSelected,
    onToggleSelect,
    view,
    meetingServiceDependencies,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    // Statuses offered in the dropdown depend on the current view — e.g. the "Not approved" tab (NOT_APPROVED view)
    // offers "Approved", mirroring the regular meeting-service page.
    const hiddenStatuses = getHiddenOrderStatuses(view)

    const [statusAnchor, setStatusAnchor] = useState<{ top: number; left: number } | null>(null)

    const invalidateOrders = async ({ queryClient }: { queryClient: QueryClient }) => {
        await queryClient.invalidateQueries({
            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
        })
    }

    const { mutate: updateLock } = useApiMutation(changeLockStatusMerchant, {
        onSuccess: (_params, _data, context) => invalidateOrders(context),
    })

    // Changing status opens the same confirmation modal used on the production/meeting-service page.
    const [pendingStatus, setPendingStatus] = useState<FilteredStatuses | null>(null)

    const allowedOrderIds = useMemo(() => {
        if (!pendingStatus) return []
        const transitions = order.orderAction.statusTransitions
        const allowed = transitions?.allowed?.[pendingStatus] || !transitions?.disallowed?.[pendingStatus]
        return allowed ? [order.id] : []
    }, [pendingStatus, order])

    const { deliveryDetails } = order
    const isLocked = !!deliveryDetails.isLocked

    const deliveryTypeLabel = l10n.getStringForEnum("delivery-type-filter", deliveryDetails.type, deliveryDetails.type)
    const isCanteenOrPickup =
        deliveryDetails.type === DeliveryType.EATING_IN_CANTEEN || deliveryDetails.type === DeliveryType.PICK_UP
    const deliveryLocationName = !isCanteenOrPickup ? deliveryDetails.location?.name : undefined

    const handleStatusClick = (event: MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        setStatusAnchor((prev) => (prev ? null : { top: rect.bottom, left: rect.right }))
    }

    const handleStatusChange = (status: FilteredStatuses) => setPendingStatus(status)

    const handleToggleLock = () => updateLock({ updateLockStatus: { isLocked: !isLocked, orderIds: [order.id] } })

    const lockTooltipId = isLocked
        ? "meeting-service-table-lock-column-lock-tooltip"
        : "meeting-service-table-lock-column-unlock-tooltip"

    return (
        <>
            <Box className={classes.headerRow}>
                <Box className={classes.headerLeft}>
                    <Checkbox
                        checked={isSelected}
                        onChange={onToggleSelect}
                        size="small"
                        className={classes.selectCheckbox}
                    />
                    <Box className={classes.titleBlock}>
                        <ProductionClampedName
                            name={order.meetingDetails?.meetingName ?? ""}
                            dialogTitleId="meeting-service-production-view-meeting-name-dialog-title"
                            titleClassName={classes.title}
                            noGrow
                        />
                        <Box className={classes.delivery}>
                            <FontAwesomeIcon icon={faTruck} className={classes.deliveryIcon} />
                            {deliveryLocationName ? (
                                <LocalizedStrict
                                    id="meeting-service-table-meeting"
                                    vars={{ locationName: deliveryLocationName }}
                                >
                                    <Typography variant="h6" component="span" className={classes.deliveryText}>
                                        {`Delivery: ${deliveryLocationName}`}
                                    </Typography>
                                </LocalizedStrict>
                            ) : (
                                <Typography variant="h6" component="span" className={classes.deliveryText}>
                                    {deliveryTypeLabel}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box className={classes.headerActions}>
                    <Box className={classes.statusButtonWrap}>
                        <MinimizedStatusButton
                            status={deliveryDetails.statusDetails.status}
                            tooltipLabel={deliveryDetails.statusDetails.message}
                            onClick={handleStatusClick}
                        />
                    </Box>
                    <StatusButton
                        currentStatus={deliveryDetails.statusDetails.status}
                        hiddenStatuses={hiddenStatuses}
                        anchorEl={statusAnchor}
                        onStatusChange={handleStatusChange}
                        handleClose={() => setStatusAnchor(null)}
                    />
                    <LocalizedStrict id={lockTooltipId} attrs={{ title: true }}>
                        <Tooltip arrow title={l10n.getString(lockTooltipId)}>
                            <IconButton size="small" className={classes.lockButton} onClick={handleToggleLock}>
                                <FontAwesomeIcon
                                    icon={isLocked ? faLock : faLockOpen}
                                    className={isLocked ? classes.lockedIcon : classes.unlockedIcon}
                                />
                            </IconButton>
                        </Tooltip>
                    </LocalizedStrict>
                </Box>
            </Box>

            {pendingStatus && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog
                        open
                        onClose={() => setPendingStatus(null)}
                        dialogTitle=""
                        maxWidthProp="lg"
                    >
                        <StatusChangeModal
                            setShowUndo={() => {}}
                            onClose={() => setPendingStatus(null)}
                            selectedStatus={pendingStatus}
                            allowedOrderIds={allowedOrderIds}
                            orders={[order]}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
        </>
    )
}
